import './license-modal.scss';

import { Box, Stack } from '@mui/material';
import { CreateLicenseField, getCreateLicenseFieldProps } from '@converge/data';
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import {
  FormCheckBoxField,
  NewFormTextField,
  NewFromSelectField,
  NewRegionFormSelectField,
  NewTypeAhead,
} from '../../../../presentation/components';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  LicenseType,
  Provider,
  useGetBrokerLicense,
  useUpsertBrokerageMutation,
} from '@converge/graphql';

import { ApolloError } from '@apollo/client';
import { CreateLicenseFieldsSchema } from './create.license.validation.schema';
import { DialogLayout } from '../../../../presentation/components/dialog-layout';
import { ILicenses } from './accordion.broker-form-container';
import { NewConvergeButton } from '../../../../presentation/components/new-button';
import { NewDateField } from '../../../../presentation';
import { useConvergeSnackBar } from '../../../../contexts';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

interface IBrokerage {
  __typename?: 'PersonEdge' | undefined;
  cursor: number;
  node: {
    __typename?: 'Person' | undefined;
    uuid?: string | null | undefined;
    agent?: boolean | null | undefined;
    firstName?: string | null | undefined;
    lastName?: string | null | undefined; // Allow `null` here
    source?: Provider | null | undefined;
    primary: boolean;
  };
}
interface AddLicenseModalProps {
  onClose: () => void;
  title: string;
  selectedLicense: ILicenses | null | undefined;
  setSelectedLicense: Dispatch<SetStateAction<ILicenses | null | undefined>>;
  associateBrokerageUuid: string;
  brokerUuid: string | undefined;
  brokerages: IBrokerage[];
}

const LICENSE_TYPE = [
  { label: 'Property and Casualty', value: LicenseType?.PropertyAndCasualty },
  { label: 'Surplus Line Tax', value: LicenseType?.SurplusLineTax },
];

interface FormData {
  [CreateLicenseField.LicenseTypeUuid]: string | undefined | null;
  [CreateLicenseField.LicenseNumber]: string | undefined | null;
  [CreateLicenseField.LicenseRegionUuid]: string | undefined | null;
  [CreateLicenseField.LicenseExpirationDate]: string | Date | null;
  [CreateLicenseField.LicenseHolderUuid]: string | undefined | null;
  [CreateLicenseField.perpetual]: boolean;
}

export const AddLicenseModal = ({
  onClose,
  title,
  brokerUuid,
  selectedLicense,
  setSelectedLicense,
  brokerages,

  associateBrokerageUuid,
}: AddLicenseModalProps) => {
  const formMethods = useForm<FormData>({
    resolver: yupResolver(CreateLicenseFieldsSchema),
    defaultValues: {},
  });

  const [isSelected, setIsSelected] = useState(false);

  const navigate = useNavigate();
  const { notify } = useConvergeSnackBar();
  const { loading, upsertEntity } = useUpsertBrokerageMutation();
  const regionUuid = formMethods.getValues(
    CreateLicenseField.LicenseRegionUuid
  );
  const LicenseHolderUuid = formMethods.getValues(
    CreateLicenseField.LicenseHolderUuid
  );
  const licenseType = formMethods.getValues(CreateLicenseField.LicenseTypeUuid);

  const {
    brokerLicenses: licenseHolderLicenses,
    loading: licenseHolderLicensesLoading,
  } = useGetBrokerLicense({
    uuid: LicenseHolderUuid ?? '',
    licenseType:
      licenseType && licenseType === LicenseType.SurplusLineTax
        ? LicenseType.SurplusLineTax
        : LicenseType.PropertyAndCasualty,
    regionUuid: regionUuid ?? '',
    enabled: Boolean(regionUuid && LicenseHolderUuid && licenseType),
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(
    selectedLicense?.node?.perpetual
      ? selectedLicense?.node?.perpetual
      : licenseHolderLicenses[0]?.perpetual
      ? licenseHolderLicenses[0]?.perpetual
      : false
  );
  useEffect(() => {
    if (licenseHolderLicenses.length > 0) {
      formMethods.setValue(
        CreateLicenseField.LicenseNumber,
        licenseHolderLicenses[0]?.label
      );
      formMethods.setValue(
        CreateLicenseField.LicenseExpirationDate,
        licenseHolderLicenses[0]?.perpetual === true
          ? null
          : licenseHolderLicenses[0]?.expirationDate ?? ''
      );
      formMethods.setValue(
        CreateLicenseField.perpetual,
        licenseHolderLicenses[0]?.perpetual ?? false
      );
      setIsDisabled(licenseHolderLicenses[0]?.perpetual ?? false);
    }
  }, [licenseHolderLicenses, formMethods]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const SubmitLicenseHandler = useCallback(
    (): SubmitHandler<FormData> => async (fields) => {
      let LicenseResponse, LicenseResponseErrors;

      const licensesBrokeData = {
        entityUuid: associateBrokerageUuid,

        persons: {
          personUuid: brokerUuid,
          licenses: [
            {
              licenseHolderUuid: isSelected ? fields.licenseHolderUuid : null,
              licenseType: fields.licenseTypeUuid,
              licenseNumber: !isSelected ? fields.licenseNumber : '',
              regionUuid: fields.licenseRegionUuid,
              expirationDate:
                !isSelected || isDisabled === true
                  ? fields.licenseExpirationDate
                  : null,
              followedLicenseUuid: isSelected
                ? licenseHolderLicenses[0]?.value
                : null,
              perpetual: !isSelected ? fields.perpetual : false,
            },
          ],
        },

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      const selectedLicenseUuid = selectedLicense?.node?.uuid;
      if (selectedLicenseUuid) {
        licensesBrokeData.persons.licenses[0].licenseUuid = selectedLicenseUuid;
      }

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: licensesBrokeData },
        });
        LicenseResponse = data;
        LicenseResponseErrors = errors;

        notify(
          `Broker license has been ${selectedLicense ? 'updated' : 'added'}`,
          {
            variant: 'success',
          }
        );
        onClose();
        navigate(`/producers/broker/${brokerUuid}`);
      } catch (cause) {
        console.error(
          `Failed to ${
            selectedLicense ? 'update' : 'save'
          } license. Please try again. (${cause})`
        );
        if (cause instanceof ApolloError) {
          LicenseResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = LicenseResponseErrors?.[0]?.message;
      if (errorMessage || !LicenseResponse) {
        notify(
          `Failed to ${
            selectedLicense ? 'update' : 'save'
          } license. Please try again. (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [
      associateBrokerageUuid,
      brokerUuid,
      isSelected,
      isDisabled,
      licenseHolderLicenses,
      selectedLicense,
      upsertEntity,
      notify,
      onClose,
      navigate,
    ]
  );

  const licenseeList = brokerages
    .filter((licensee) =>
      isSelected ? licensee.node.uuid !== brokerUuid : true
    )
    .map((licensee) => ({
      label: `${licensee.node.firstName} ${licensee.node.lastName}`,
      value: licensee.node.uuid,
    }));

  useEffect(() => {
    if (
      isSelected === true &&
      LicenseHolderUuid &&
      LicenseHolderUuid.length > 0
    ) {
      console.log('licenseSelected');
    } else if (selectedLicense && !formMethods?.formState?.isDirty) {
      formMethods.setValue(
        CreateLicenseField.LicenseExpirationDate,
        selectedLicense?.node?.expirationDate
          ? selectedLicense.node.expirationDate
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseRegionUuid,
        selectedLicense ? selectedLicense?.node?.region?.uuid : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseNumber,
        selectedLicense ? selectedLicense?.node?.licenseNumber : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseTypeUuid,
        selectedLicense ? selectedLicense?.node?.licenseType : ''
      );
      formMethods.setValue(
        CreateLicenseField.perpetual,
        selectedLicense && selectedLicense?.node?.perpetual
          ? selectedLicense?.node?.perpetual
          : false
      );
    }
  }, [isSelected, LicenseHolderUuid, formMethods, selectedLicense]);

  useEffect(() => {
    if (isSelected === false) {
      formMethods.setValue(
        CreateLicenseField.LicenseHolderUuid,
        brokerUuid ?? ''
      );
    } else if (selectedLicense) {
      formMethods.setValue(
        CreateLicenseField.LicenseExpirationDate,
        selectedLicense?.node?.expirationDate
          ? selectedLicense.node.expirationDate
          : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseRegionUuid,
        selectedLicense ? selectedLicense?.node?.region?.uuid : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseNumber,
        selectedLicense ? selectedLicense?.node?.licenseNumber : ''
      );
      formMethods.setValue(
        CreateLicenseField.LicenseTypeUuid,
        selectedLicense ? selectedLicense?.node?.licenseType : ''
      );
    }
  }, [isSelected, formMethods, brokerUuid, selectedLicense]);

  useEffect(() => {
    if (
      selectedLicense &&
      selectedLicense?.node.licenseHolder &&
      selectedLicense?.node.licenseHolder?.uuid !== brokerUuid
    ) {
      setIsSelected(true);
    }
  }, [selectedLicense, brokerUuid]);

  return (
    <DialogLayout
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          maxWidth: '100px',
          minWidth: '900px',

          overflow: 'hidden !important',
          fontFamily: 'Red Hat Text, Arial, sans-serif',
        },
        '& .MuiDialogContent-root ': {
          overflow: 'hidden !important',
        },
        '& .MuiDialogContent-root': {
          marginLeft: '0px',
        },
        '&.MuiTypography-root.MuiTypography-h6': {
          marginTop: '0px',
          marginRight: '0px',
        },
        '&.uiButtonBase-root.MuiIconButton-root.MuiIconButton-sizeSmall': {
          padding: '0px',
        },
        '& .MuiPaper-root': {
          boxShadow: 'none', // Example style
          overflow: 'hidden',
        },
        '& .MuiPaper-elevation': {
          elevation: 3, // You can change the elevation
        },
        '& .MuiPaper-rounded': {
          borderRadius: '8px', // Customize the border radius
        },
        '& .MuiPaper-elevation24': {
          boxShadow: '0px 4px 10px rgba(0,0,0,0.3)', // Customize the shadow
        },
        '& .MuiDialog-paper': {
          padding: '0px 5px', // Example padding
        },
        '& .MuiDialog-paperScrollPaper': {
          overflowY: 'hidden', // Allow scrolling for long content
        },
        '& .MuiDialog-paperWidthSm': {
          maxWidth: '730px !important', // Apply max-width as you asked
        },
      }}
      open
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      header={<Box className="modal-title-license"> {title}</Box>}
      className="add-broker-license-modal"
    >
      <FormProvider {...formMethods}>
        <form>
          {!selectedLicense && (
            <>
              <Box className="checkbox-license-modal-container">
                <FormCheckBoxField
                  dataTestId="license-under-another-broker-checkbox"
                  customFont="16px"
                  name="License under another broker"
                  label="License under another broker"
                  handleChange={(event) => {
                    const checked = event.target.checked;
                    setIsSelected(checked);
                    formMethods.clearErrors(
                      CreateLicenseField.LicenseExpirationDate
                    );
                    formMethods.clearErrors(CreateLicenseField.LicenseNumber);
                  }}
                />
              </Box>
            </>
          )}
          <Stack className="modal-container">
            <Box className="fields-right">
              <NewTypeAhead
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseHolderUuid
                )}
                placeholder="Select Holder"
                dataTestId="license-holder-dropdown"
                options={licenseeList}
                disabled={!isSelected || Boolean(selectedLicense)}
              />
            </Box>
            <Box className="fields-right">
              <NewFromSelectField
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseTypeUuid
                )}
                disabled={Boolean(selectedLicense)}
                options={LICENSE_TYPE}
                required
                placeholder="Select Type"
                dataTestId="license-type-dropdown"
              />
            </Box>

            <Box className="fields-left">
              <NewRegionFormSelectField
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseRegionUuid
                )}
                disabled={Boolean(selectedLicense)}
                dataTestId="license-state-dropdown"
                required
              />
            </Box>
          </Stack>
          <Stack className="modal-container">
            <Box className=" big-fields-right">
              <Box
                sx={{
                  ...(isSelected &&
                    !licenseHolderLicensesLoading &&
                    Boolean(regionUuid && LicenseHolderUuid && licenseType) &&
                    licenseHolderLicenses.length === 0 && {
                      '& .new-coverage-input-label': {
                        color: '#d32f2f !important',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '1px !important',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '1px !important',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '2px !important',
                        },
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '1px !important',
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '1px !important',
                        },
                        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                          borderWidth: '1px !important',
                        },
                        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: '#d32f2f !important',
                            borderWidth: '2px !important',
                          },
                        '&.Mui-error.Mui-disabled .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: '#d32f2f !important',
                            borderWidth: '1px !important',
                          },
                      },
                    }),
                }}
              >
                <NewFormTextField
                  {...getCreateLicenseFieldProps(
                    CreateLicenseField.LicenseNumber
                  )}
                  placeholder="Enter License Number"
                  disabled={isSelected || Boolean(selectedLicense)}
                  dataTestId="license-number-input"
                  required
                  value={
                    formMethods.getValues(CreateLicenseField.LicenseNumber) ??
                    ''
                  }
                  error={
                    isSelected &&
                    !licenseHolderLicensesLoading &&
                    Boolean(regionUuid && LicenseHolderUuid && licenseType) &&
                    licenseHolderLicenses.length === 0
                  }
                />
              </Box>
              {isSelected &&
                !licenseHolderLicensesLoading &&
                Boolean(regionUuid && LicenseHolderUuid && licenseType) &&
                licenseHolderLicenses.length === 0 && (
                  <Box
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      lineHeight: 1.66,
                      letterSpacing: '0.03333em',
                      textAlign: 'left',
                      marginTop: '3px',
                      marginRight: '14px',
                      marginBottom: 0,
                      marginLeft: '0px',
                    }}
                    data-testid="license-number-error-message"
                  >
                    No license number available for that holder, type, and state
                  </Box>
                )}
            </Box>
            <Box className="big-fields-left perpetual-container">
              <NewDateField
                key={
                  formMethods.watch(CreateLicenseField.perpetual)
                    ? 'perpetual-true'
                    : 'perpetual-false'
                }
                {...getCreateLicenseFieldProps(
                  CreateLicenseField.LicenseExpirationDate
                )}
                disabled={isSelected || isDisabled === true}
                dataTestId="license-expiration-date-input"
                minDate={today}
                required
              />
              <Box style={{ marginLeft: '28px', marginTop: '25px' }}>
                <FormCheckBoxField
                  dataTestId="perpetual-license-checkbox"
                  customFont="14px"
                  name={CreateLicenseField.perpetual}
                  label="Perpetual"
                  disabled={isSelected}
                  handleChange={(event) => {
                    const checked = event.target.checked;
                    setIsDisabled(checked);

                    formMethods.setValue(CreateLicenseField.perpetual, checked);
                    if (checked) {
                      formMethods.setValue(
                        CreateLicenseField.LicenseExpirationDate,
                        null
                      );
                      formMethods.clearErrors(
                        CreateLicenseField.LicenseExpirationDate
                      );
                    }
                  }}
                  checked={isDisabled}
                />
              </Box>
            </Box>
          </Stack>
          <Stack className="button-container-broker-license">
            <Box className="close-btn-Container">
              <NewConvergeButton
                label="CANCEL"
                variant="outlined"
                onClick={onClose}
                dataTestId="cancel-license-button"
              />
            </Box>
            <Box>
              <NewConvergeButton
                loading={loading || licenseHolderLicensesLoading}
                onClick={formMethods.handleSubmit(SubmitLicenseHandler())}
                dataTestId={
                  selectedLicense
                    ? 'update-license-button'
                    : 'add-license-modal-button'
                }
                label={`${selectedLicense ? 'UPDATE' : 'ADD'} LICENSE`}
                disabled={
                  isSelected &&
                  !licenseHolderLicensesLoading &&
                  Boolean(regionUuid && LicenseHolderUuid && licenseType) &&
                  licenseHolderLicenses.length === 0
                }
              />
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </DialogLayout>
  );
};
